// Production reading page. Composes the three hooks, the Prose tree, and
// the Rail. Wires scrollRef / mainRef and feeds them to every hook. See
// `frontend/src/demo/DemoPage.tsx` for the slice-1 reference wiring.

import { useCallback, useEffect, useMemo, useRef } from "react";
import {
  useTermCrossHighlight,
  useActiveGlossaryTerms,
  useActiveSection,
  useMobileMechanic,
  goToSection,
  type TermEntry,
} from "../reading";
import "../reading/reading.css";
import "../tokens/accents.css";
import "./reading-page.css";
import type {
  CorpusConfig,
  CorpusGlossary,
  DocJSON,
  PublishedGlossary,
} from "../data/types";
import { CORPUS_CONFIG_DEFAULTS } from "../data/types";
import { applyConfig } from "./applyConfig";
import { buildCanonicalResolver } from "./canonicalResolver";
import { Prose } from "./Prose";
import { Rail } from "./Rail";
import { DrillRail } from "./DrillRail";
import { PrintFallback } from "./PrintFallback";
import { ChipStrip } from "./ChipStrip";
import { TermSheet } from "./TermSheet";
import { SectionPill } from "./SectionPill";
import { SectionSheet } from "./SectionSheet";

export interface ReadingPageProps {
  doc: DocJSON;
  glossary: PublishedGlossary;
  // PRD 08 — supplied by the render-endpoint composite payload. Falls back
  // to DEFAULTS so slice-1 fixture rendering still works.
  config?: CorpusConfig;
  // PRD 07 — optional. Present when equation extraction has run for this
  // doc. Absent → Prose falls back to plain KaTeX per the forward-compat
  // invariant; absence is the API-docs-corpus common case.
  equations?: import("../data/types").EquationEntry[];
  // 00_processor §Pass 2 — when Pass 2 has run for the doc's parent
  // corpus, the canonical entity index ships in the payload. The
  // renderer prefers the canonical Definition over the per-doc one for
  // any term/symbol whose ref appears in some entity's member_refs.
  corpusGlossary?: CorpusGlossary | null;
}

export function ReadingPage({
  doc,
  glossary,
  config = CORPUS_CONFIG_DEFAULTS,
  equations,
  corpusGlossary,
}: ReadingPageProps) {
  const canonicalResolver = useMemo(
    () => buildCanonicalResolver(corpusGlossary),
    [corpusGlossary],
  );
  const scrollRef = useRef<HTMLDivElement>(null);
  const mainRef = useRef<HTMLElement>(null);

  // Apply config to the document root so attribute selectors in
  // accents.css resolve. This is global by design — the hosted reader
  // owns the page. The widget builds set attrs on the shadow host instead.
  useEffect(() => {
    if (typeof document === "undefined") return;
    applyConfig(document.documentElement, config);
  }, [config]);

  const allTerms: TermEntry[] = useMemo(
    () =>
      glossary.terms.map((t) => ({
        term: t.term,
        termId: t.term_id,
        alternates: t.alternates,
        kind: t.kind,
        termFamily: t.term_family,
        includes: t.includes,
      })),
    [glossary],
  );
  const allTermStrings = useMemo(() => allTerms.map((t) => t.term), [allTerms]);

  useTermCrossHighlight(scrollRef, mainRef, allTerms);
  const activeTerms = useActiveGlossaryTerms(scrollRef, mainRef, allTermStrings);
  const activeSectionId = useActiveSection(scrollRef, mainRef);

  const onSectionSelect = useCallback(
    (id: string) => goToSection(scrollRef.current, id),
    [],
  );

  const mobile = useMobileMechanic({
    doc, glossary, scrollRef, activeSectionId, activeTerms,
  });

  const mode = config.rail_position;
  const railNode = (
    <Rail
      doc={doc}
      glossary={glossary}
      activeSectionId={activeSectionId}
      activeTerms={activeTerms}
      canonicalResolver={canonicalResolver}
      onSectionSelect={onSectionSelect}
    />
  );

  return (
    <div ref={scrollRef} className="maisight-scroll maisight-reading-scroll">
      <div className="maisight-reading-layout" data-rail-mode={mode}>
        {(mode === "left" || mode === "split") && railNode}
        <main ref={mainRef} className="maisight-reading-main">
          {doc.title ? (
            <header className="maisight-reading-header">
              <h1 className="maisight-reading-title">{doc.title}</h1>
            </header>
          ) : null}
          <Prose
            doc={doc}
            equations={equations}
            canonicalResolver={canonicalResolver}
          />
        </main>
        {mode === "right" && railNode}
        {mode === "split" && <DrillRail mode="split" />}
      </div>
      <TermSheet
        entry={mobile.selectedEntry}
        canonical={
          mobile.selectedEntry
            ? canonicalResolver?.byRef(doc.doc_id, mobile.selectedEntry.term_id) ?? null
            : null
        }
        onClose={mobile.closeTermSheet}
      />
      <SectionPill
        sections={doc.sections}
        activeSectionId={activeSectionId}
        onOpen={mobile.openToc}
      />
      <SectionSheet
        open={mobile.tocOpen}
        sections={doc.sections}
        activeSectionId={activeSectionId}
        onSelect={onSectionSelect}
        onClose={mobile.closeToc}
      />
      <ChipStrip
        terms={mobile.visibleTerms}
        selectedTermId={mobile.selectedTermId}
        onSelect={mobile.onChipSelect}
      />
      <PrintFallback />
    </div>
  );
}
